import {
  Pressable, StyleSheet, Text, View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import React from 'react';
import { useDispatch } from 'react-redux';
import Colors from '../../constants/Colors';
import RightArrow24 from '../24pxIcons/RightArrow24';
import CompletedIcon from '../40pxIcons/CompletedIcon';
import FlagIcon40 from '../40pxIcons/FlagIcon';
import InProgressIcon from '../40pxIcons/InProgressIcon';
import PausedStatusIcon from '../40pxIcons/PausedStatusIcon';
import getPriorityColor from '../PriorityColorScheme';
import { logEvent } from '../LogEvent';
import {
  DEFAULT_TICKET_PRIORITY, Ticket, ticketPriorityIsValid, TicketStatus,
} from '../../constants/Types';
import { updateSelectedTicket } from '../../redux/selectedTicketSlice';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import isMobile from '../IsMobile';

interface TicketRowProps {
  index: number;
  ticket: Ticket;
  slaNames: string[];
  onPress?: () => void;
}

const getPriorityName = (slaNames: string[], index: number) => `P${index + 1} - ${slaNames[index]}`;

const TicketRow = ({
  index, ticket, slaNames, onPress,
}: TicketRowProps) => {
  const navigation = useNavigation();
  const { status } = ticket;
  let { priority } = ticket;
  if (!ticketPriorityIsValid(priority)) priority = DEFAULT_TICKET_PRIORITY;
  const dispatch = useDispatch();

  const openTicket = () => {
    if (onPress) {
      onPress();
      return;
    }
    logEvent('TicketRow', 'open');
    const ticketWithoutAsset = { ...ticket };
    ticketWithoutAsset.asset = null;
    dispatch(updateSelectedTicket({ ...ticketWithoutAsset, pinned: true }));
    if (isMobile) {
      navigation.navigate('Root' as any, { screen: AppScreen.TICKETS });
    } else {
      navigation.navigate(AppScreen.TICKETS as any);
    }
  };

  return (
    <Pressable
      style={(state) => [
        styles.container,
        { zIndex: index * -1, opacity: state.pressed ? 0.7 : 1 },
      ]}
      onPress={openTicket}
      testID={`side-menu-ticket#${index + 1}`}
    >
      <View>
        {status === TicketStatus.INPROGRESS && <InProgressIcon style={styles.checkmarkStyle} />}
        {status === TicketStatus.PAUSED && <PausedStatusIcon style={styles.checkmarkStyle} />}
        {status === TicketStatus.COMPLETED && <CompletedIcon style={styles.checkmarkStyle} />}
        {(status === TicketStatus.REPORTED || (status !== TicketStatus.INPROGRESS
          && status !== TicketStatus.PAUSED && status !== TicketStatus.COMPLETED))
          && <FlagIcon40 style={styles.checkmarkStyle} />}
        {ticketPriorityIsValid(priority) && <View
          style={[
            styles.priorityCircle,
            { backgroundColor: getPriorityColor(priority) },
          ]}
        />}
      </View>
      <View style={styles.nameContainer}>
        <Text style={styles.name}>
          {ticket.name}
        </Text>
        {ticketPriorityIsValid(priority) && <Text style={styles.priorityText}>
          {getPriorityName(slaNames, priority)}
        </Text>}
      </View>
      <View
        style={{ paddingHorizontal: 10 }}
        testID='map-navigate-to-ticket-btn'
      >
        <RightArrow24 />
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
    paddingLeft: 10,
    marginHorizontal: 16,
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  nameContainer: {
    flex: 1,
    paddingVertical: 12,
    borderRightWidth: 1,
    marginLeft: 8,
    borderColor: Colors.light.hereworksBlue100,
  },
  name: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    lineHeight: 21,
  },
  priorityText: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 12,
    color: Colors.light.hereworksBlack400,
  },
  priorityCircle: {
    position: 'absolute',
    top: 2,
    left: 2,
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 3,
    borderColor: '#fff',
  },
  checkmarkStyle: {
    marginRight: 20,
  },
});

export default TicketRow;
